import { useFeatureLocks } from '@/hooks/useFeatureLocks';
import useIsComponentMounted from '@/utils/useIsComponentMounted';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CartButton } from '@/components/DesignSystem/AtomicComponents';
import NotifyProduct from '@/components/NotifyProduct';
import { roundnessCalculator } from '@/components/DesignSystem/AtomicComponents/util';
import { deviceWidth } from '@/utils/deviceWidth';
import { isNotifyProductEnabled } from '@/utils/isNotifyProductEnabled';
import { MAX_MOBILE_WIDTH } from '@/utils/constants';
import getCustomCtaText from '@/utils/getCustomCtaText';
import { MobileOutOfStockContainer, MobileOutOfStockText } from './CartCta.styles';
import LeftCartButton from './LeftCartButton';
import RightCartButton from './RightCartButton';
import { checkProductPersonalizationExist } from '../../Utils/common';
import { useCartHandlers } from '../../../../hooks/useCartHandler';
import { setUnavailableQuantityAlertState } from 'src/redux/actions';

function CartCtaMobile(props) {
  const {
    handleCart,
    goToCart,
    itemIndexInCart,
    onBuyNowClick,
    returnItemData,
    disabledAdditionComponent,
    variantOutOfStock,
    product,
    schedualerConstraints,
    scheduleInfo,
    setShowSchedulerError,
  } = props;
  const dispatch = useDispatch();
  const isMobile = deviceWidth <= MAX_MOBILE_WIDTH;
  const { storeInfo } = useSelector((state) => ({
    storeInfo: state.storeReducer.store,
  }));
  const { notifyMeFeatureStatus } = useFeatureLocks();
  const isNotifyMeFeatureVisible = notifyMeFeatureStatus?.isVisible;
  const isComponentMounted = useIsComponentMounted();

  const {
    handleIncreamentClick,
    handleDecreamentClick,
    handleSetProductData,
    handleSetProductCustomizationCount,
    onPersonalizeProductClick,
  } = useCartHandlers();

  const isProductPersonalizationAvailable = checkProductPersonalizationExist(
    storeInfo?.services?.store_type_flag,
    product?.product_personalization
  );

  /**
   * This function is responsible to perform 3 tasks:
   * 1. Set the active product data in personalisedProduct state of catalogReducer
   * 2. Set the product customization config ( image count, text count etc ) in personalisedProduct state of catalogReducer
   * 3. Open the product personalization popup
   */
  function openProductPersonalizationPopUp() {
    handleSetProductData(product);
    handleSetProductCustomizationCount(product);
    onPersonalizeProductClick();
  }

  function handleCartAction(type) {
    if (isProductPersonalizationAvailable) {
      type === 'inc'
        ? handleIncreamentClick(type, product)
        : handleDecreamentClick(type, product);
    } else {
      handleCart(type);
    }
  }

  function renderOutOfStock() {
    return (
      <MobileOutOfStockContainer>
        <MobileOutOfStockText>Out Of Stock</MobileOutOfStockText>
      </MobileOutOfStockContainer>
    );
  }

  function personalizeAndBuyCTA() {
    return (
      <Button
        borderColor="#EDEDED"
        borderWidth="0px"
        label={
          storeInfo?.theme?.cta_config?.pdp_personalized_cta || 'Personalise and buy'
        }
        backgroundColor={storeInfo?.theme?.colors?.primary_color}
        fontSize="14px"
        px="30px"
        py="15px"
        my="0"
        mx="auto"
        className="!tw-w-[100%] !tw-text-[0.875rem] !tw-font-normal"
        roundness={storeInfo?.theme?.roundness}
        onClick={() => {
          if (schedualerConstraints && !scheduleInfo) {
            setShowSchedulerError(true);
            return;
          }
          handleSetProductData(product);
          handleSetProductCustomizationCount(product);
          onPersonalizeProductClick();
        }}
      />
    );
  }

  if (!isMobile) return null;
  if (
    isNotifyMeFeatureVisible &&
    isNotifyProductEnabled() &&
    (variantOutOfStock || product.available === 0)
  )
    return (
      <div className="tw-pt-3">
        <NotifyProduct product={product} />
      </div>
    );

  if (product.available === 0) return renderOutOfStock();

  if (variantOutOfStock) return renderOutOfStock();

  const handleItemChange = (type, count = null, reset = null) => {
    const selectedProductInfo = returnItemData().selectedItem;
    if (
      count &&
      selectedProductInfo?.managed_inventory &&
      selectedProductInfo?.available_quantity < count
    ) {
      dispatch(
        setUnavailableQuantityAlertState({
          show: true,
          availableQuantity: selectedProductInfo?.available_quantity,
          onConfirm: () => handleCart(type, selectedProductInfo?.available_quantity),
          onCancel: reset,
        })
      );
    } else {
      handleCart(type, count);
    }
  };

  const renderCTA = () => {
    if (storeInfo?.services?.store_flag === 0) {
      return null;
    }

    if (isProductPersonalizationAvailable && itemIndexInCart === -1) {
      return (
        <article className="box--price">
          <div className="box--addToCart-btn w-100 flex justify-center items-center">
            {' '}
            {personalizeAndBuyCTA()}{' '}
          </div>
        </article>
      );
    }

    return (
      <article className="box--price">
        <div className="box--addToCart-btn w-100 flex justify-center items-center">
          {itemIndexInCart === -1 && isComponentMounted ? (
            <Button
              label="Add to Cart"
              variant="outline"
              borderColor="#EDEDED"
              backgroundColor={storeInfo?.theme?.colors?.primary_color}
              fontSize="18"
              px="30px"
              py="15px"
              mr="8px"
              roundness={storeInfo?.theme?.roundness}
              onClick={() => {
                if (schedualerConstraints && !scheduleInfo) {
                  setShowSchedulerError(true);
                  return;
                }
                isProductPersonalizationAvailable
                  ? openProductPersonalizationPopUp()
                  : handleCart('add');
              }}
            />
          ) : (
            <CartButton
              allowCustomInput={!product?.product_personalization}
              leftButton={
                <LeftCartButton
                  isPDPPage={true}
                  iconWidth={14}
                  handleCart={handleCartAction}
                />
              }
              rightButton={
                <RightCartButton
                  isPDPPage={true}
                  disabledAdditionComponent={disabledAdditionComponent}
                  handleCart={handleCartAction}
                  iconWidth={14}
                />
              }
              label={
                isProductPersonalizationAvailable
                  ? storeInfo?.theme?.cta_config?.pdp_personalized_cta ||
                    'Personalise and buy'
                  : null
              }
              fontSize="14"
              height="48px"
              roundness={storeInfo?.theme?.roundness}
              onChange={handleItemChange}
              quantity={returnItemData().quantity}
              px="5px"
              py="14px"
              mr="8px"
              customClasses="!tw-w-full !tw-justify-center"
              wrapperClasses=" !tw-h-[48px]"
            />
          )}
          {itemIndexInCart === -1 && isComponentMounted ? (
            <Button
              borderColor="#EDEDED"
              label={
                storeInfo?.theme?.cta_config?.pdp_cta || getCustomCtaText('buy_now_cta')
              }
              backgroundColor={storeInfo?.theme?.colors?.primary_color}
              fontSize="18"
              px="30px"
              py="15px"
              roundness={storeInfo?.theme?.roundness}
              onClick={() => {
                if (schedualerConstraints && !scheduleInfo) {
                  setShowSchedulerError(true);
                  return;
                }
                isProductPersonalizationAvailable
                  ? openProductPersonalizationPopUp()
                  : onBuyNowClick();
              }}
            />
          ) : (
            <div
              className="go-to-cart__btn"
              style={{
                borderRadius: `${roundnessCalculator(storeInfo?.theme?.roundness)}`,
                backgroundColor: `${storeInfo?.theme?.colors?.primary_color}`,
                height: '48px',
                fontSize: '14px',
              }}
              onClick={goToCart}
            >
              Go to Cart
            </div>
          )}
        </div>
      </article>
    );
  };

  return <>{renderCTA()}</>;
}

export default CartCtaMobile;
